import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bell, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { format } from "date-fns";

interface Announcement {
  id: number;
  title: string;
  message: string;
  priority: "info" | "warning" | "urgent";
  tags: string[];
  effectiveFrom: string;
  effectiveTo: string | null;
  createdAt: string;
  createdBy: string;
}

const priorityConfig = {
  info: {
    icon: Info,
    color: "bg-blue-100 text-blue-800 border-blue-200",
    badgeVariant: "default" as const,
  },
  warning: {
    icon: AlertTriangle,
    color: "bg-yellow-100 text-yellow-800 border-yellow-200",
    badgeVariant: "secondary" as const,
  },
  urgent: {
    icon: AlertCircle,
    color: "bg-red-100 text-red-800 border-red-200",
    badgeVariant: "destructive" as const,
  },
};

export default function Announcements() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["/api/parent/announcements"],
    queryFn: () => api.get("/api/parent/announcements"),
  });

  const announcements: Announcement[] = data?.data || [];

  if (isLoading) {
    return (
      <div className="container mx-auto p-4 md:p-6 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Announcements</h1>
        </div>
        <Card data-testid="announcements-loading">
          <CardContent className="p-6">
            <p className="text-muted-foreground">Loading announcements...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 md:p-6 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Announcements</h1>
        </div>
        <Card data-testid="announcements-error">
          <CardContent className="p-6">
            <p className="text-red-600">Failed to load announcements. Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    // Outer container keeps the legacy `announcements-page` testid used by
    // tests/parent-announcements-mvp.e2e.spec.ts and tests/announcements.e2e.spec.ts.
    // The header row additionally carries `parent-announcements-page` for the
    // launch cert (tests/launch/parent-announcements.launch.e2e.spec.ts:28),
    // which needs a stable always-visible target — `announcements-list` only
    // renders when the parent has at least one announcement.
    <div className="container mx-auto p-4 md:p-6 max-w-4xl" data-testid="announcements-page">
      <div className="flex items-center gap-3 mb-6" data-testid="parent-announcements-page">
        <Bell className="h-8 w-8 text-primary" />
        <h1 className="text-3xl font-bold">Announcements</h1>
      </div>

      {announcements.length === 0 ? (
        <Card data-testid="announcements-empty">
          <CardContent className="p-8 text-center">
            <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No announcements</h3>
            <p className="text-muted-foreground">
              There are no active announcements at this time. Check back later for updates.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="announcements-list">
          {announcements.map((announcement) => {
            const config = priorityConfig[announcement.priority];
            const Icon = config.icon;

            return (
              <Card
                key={announcement.id}
                className={`border-l-4 ${config.color}`}
                data-testid={`announcement-item-${announcement.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2" data-testid={`announcement-title-${announcement.id}`}>
                          {announcement.title}
                        </CardTitle>
                        <div className="flex flex-wrap gap-2 items-center text-sm text-muted-foreground">
                          <Badge variant={config.badgeVariant} data-testid={`announcement-priority-${announcement.id}`}>
                            {announcement.priority.toUpperCase()}
                          </Badge>
                          <span>•</span>
                          <span>{format(new Date(announcement.createdAt), "MMM dd, yyyy")}</span>
                          {announcement.tags && announcement.tags.length > 0 && (
                            <>
                              <span>•</span>
                              <div className="flex gap-1">
                                {announcement.tags.map((tag, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed" data-testid={`announcement-message-${announcement.id}`}>
                    {announcement.message}
                  </p>
                  {announcement.effectiveTo && (
                    <p className="text-xs text-muted-foreground mt-3">
                      Valid until {format(new Date(announcement.effectiveTo), "MMM dd, yyyy")}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
